/**
 * API Client Configuration for Doctrine
 *
 * Provides typed API access using TanStack Query.
 * Backend runs on port 3003, frontend proxies /api requests.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// User identity storage keys
const USER_ID_KEY = 'sled_user_id'
const USER_LABEL_KEY = 'sled_user_label'

/**
 * Get or generate the user ID for staged imports
 */
export function getUserId(): string {
  let userId = localStorage.getItem(USER_ID_KEY)
  if (!userId) {
    userId = crypto.randomUUID()
    localStorage.setItem(USER_ID_KEY, userId)
  }
  return userId
}

/**
 * Get the optional user label
 */
export function getUserLabel(): string | null {
  return localStorage.getItem(USER_LABEL_KEY)
}

/**
 * Set the optional user label
 */
export function setUserLabel(label: string): void {
  localStorage.setItem(USER_LABEL_KEY, label)
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  /** Timeout in milliseconds. Default: 30000 (30 seconds). Use 0 for no timeout. */
  timeout?: number
}

/**
 * Base fetch wrapper with error handling and configurable timeout
 */
async function apiFetch<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, timeout = 30000 } = options

  // Set up AbortController for timeout
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  if (timeout > 0) {
    timeoutId = setTimeout(() => controller.abort(), timeout)
  }

  const requestHeaders: Record<string, string> = { ...headers }
  if (body) {
    requestHeaders['Content-Type'] = 'application/json'
  }

  // Add user ID header for staged imports and tracking
  const userId = getUserId()
  if (userId) {
    requestHeaders['X-User-Id'] = userId
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    signal: controller.signal,
  }

  if (body) {
    config.body = JSON.stringify(body)
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An error occurred' }))
      throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeout / 1000} seconds`)
    }
    throw error
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * API client object with typed methods for each endpoint
 */
export const api = {
  // Settings
  settings: {
    getScoring: () => apiFetch<ScoringWeights>('/api/lead-eval/settings/scoring'),
    updateScoring: (data: ScoringWeights) =>
      apiFetch<ScoringWeights>('/api/lead-eval/settings/scoring', { method: 'PUT', body: data }),
    // API Key Settings
    getApiKeys: () =>
      apiFetch<ApiKeySettings>('/api/settings/api-keys'),
    updateApiKey: (provider: ApiProvider, apiKey: string | null, useCustom: boolean) =>
      apiFetch<ApiKeySettings>('/api/settings/api-keys', {
        method: 'PUT',
        body: { provider, apiKey, useCustom },
      }),
    deleteApiKey: (provider: ApiProvider) =>
      apiFetch<ApiKeySettings>(`/api/settings/api-keys/${provider}`, { method: 'DELETE' }),
    validateApiKey: (provider: ApiProvider, apiKey: string) =>
      apiFetch<ApiKeyValidationResult>('/api/settings/api-keys/validate', {
        method: 'POST',
        body: { provider, apiKey },
      }),

    // Kaiju Images Management
    getKaijuImages: () =>
      apiFetch<KaijuImagesResponse>('/api/settings/kaiju-images'),

    uploadKaijuImage: async (file: File): Promise<KaijuImageUploadResponse> => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`${API_BASE_URL}/api/settings/kaiju-images`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }))
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`)
      }

      return response.json()
    },

    deleteKaijuImage: (filename: string) =>
      apiFetch<void>(`/api/settings/kaiju-images/${filename}`, { method: 'DELETE' }),

    // Agent Mode toggles
    getAgentMode: () =>
      apiFetch<AgentModeSettings>('/api/settings/agent-mode'),
    updateAgentMode: (feature: AgentModeFeature, enabled: boolean) =>
      apiFetch<AgentModeSettings>('/api/settings/agent-mode', {
        method: 'PUT',
        body: { feature, enabled },
      }),

    // NotebookLM Authentication
    notebooklm: {
      status: () =>
        apiFetch<NotebookLmStatus>('/api/settings/notebooklm/status', { timeout: 15000 }),
      startAuth: () =>
        apiFetch<{ started: boolean; message: string }>('/api/settings/notebooklm/auth/start', { method: 'POST' }),
      pollAuth: () =>
        apiFetch<{ completed: boolean; waiting?: boolean; error?: string }>('/api/settings/notebooklm/auth/poll'),
      pasteCookies: (cookies: string) =>
        apiFetch<{ saved: boolean; cookieCount: number }>('/api/settings/notebooklm/auth/cookies', { method: 'POST', body: { cookies } }),
      reconnect: () =>
        apiFetch<{ connected: boolean; tools?: number; error?: string }>('/api/settings/notebooklm/reconnect', { method: 'POST', timeout: 30000 }),
    },
  },

  // Chat
  chat: {
    sessions: {
      list: (params?: { scope?: string; entityId?: string }) => {
        const query = new URLSearchParams()
        if (params?.scope) query.set('scope', params.scope)
        if (params?.entityId) query.set('entityId', params.entityId)
        const qs = query.toString()
        return apiFetch<{ sessions: ChatSessionResponse[]; total: number }>(`/api/chat/sessions${qs ? `?${qs}` : ''}`)
      },
      create: (data: { scope: string; entityType?: string; entityId?: string; entityName?: string; useMemory?: boolean }) =>
        apiFetch<{ session: ChatSessionResponse }>('/api/chat/sessions', { method: 'POST', body: data }),
      get: (id: string) =>
        apiFetch<{ session: ChatSessionResponse }>(`/api/chat/sessions/${id}`),
      delete: (id: string) =>
        apiFetch<void>(`/api/chat/sessions/${id}`, { method: 'DELETE' }),
    },
    messages: {
      list: (sessionId: string) =>
        apiFetch<{ messages: ChatMessageResponse[] }>(`/api/chat/sessions/${sessionId}/messages`),
    },
    knowledge: {
      list: () =>
        apiFetch<{ success: boolean; data: { docs: ChatKnowledgeDocResponse[]; total: number } }>('/api/chat/knowledge').then(res => res.data),
      create: (data: { title: string; content: string; docType?: string; tags?: string[]; sourceUrl?: string; versionInfo?: string }) =>
        apiFetch<{ doc: ChatKnowledgeDocResponse }>('/api/chat/knowledge', { method: 'POST', body: data }),
      get: (id: string) =>
        apiFetch<{ doc: ChatKnowledgeDocResponse }>(`/api/chat/knowledge/${id}`),
      update: (id: string, data: any) =>
        apiFetch<{ updated: boolean }>(`/api/chat/knowledge/${id}`, { method: 'PUT', body: data }),
      delete: (id: string) =>
        apiFetch<void>(`/api/chat/knowledge/${id}`, { method: 'DELETE' }),
      importUrl: (data: { url: string; title?: string; docType?: string; tags?: string[]; versionInfo?: string }) =>
        apiFetch<{ doc: ChatKnowledgeDocResponse; contentLength: number }>('/api/chat/knowledge/import-url', { method: 'POST', body: data, timeout: 60000 }),
      search: (query: string) =>
        apiFetch<{ content: string; citations: string[]; query: string }>('/api/chat/knowledge/search', { method: 'POST', body: { query }, timeout: 30000 }),
      uploadFile: async (file: File, meta?: { title?: string; docType?: string; tags?: string; versionInfo?: string }) => {
        const formData = new FormData()
        formData.append('file', file)
        if (meta?.title) formData.append('title', meta.title)
        if (meta?.docType) formData.append('docType', meta.docType)
        if (meta?.tags) formData.append('tags', meta.tags)
        if (meta?.versionInfo) formData.append('versionInfo', meta.versionInfo)
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 120000)
        try {
          const response = await fetch(`${API_BASE_URL}/api/chat/knowledge/upload`, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
          })
          if (!response.ok) {
            const err = await response.json().catch(() => ({ error: 'Upload failed' }))
            throw new Error(err.error || `HTTP ${response.status}`)
          }
          return response.json()
        } finally {
          clearTimeout(timeoutId)
        }
      },
    },
    facts: {
      list: (params: { entityId: string; status?: string; category?: string }) => {
        const query = new URLSearchParams()
        query.set('entityId', params.entityId)
        if (params.status) query.set('status', params.status)
        if (params.category) query.set('category', params.category)
        return apiFetch<{ data: any[]; total: number }>(`/api/chat/facts?${query.toString()}`)
      },
      promote: (factId: string) =>
        apiFetch<{ data: any }>(`/api/chat/facts/${factId}/promote`, { method: 'POST' }),
      supersede: (factId: string, newFactId: string) =>
        apiFetch<{ data: any }>(`/api/chat/facts/${factId}/supersede`, { method: 'POST', body: { newFactId } }),
      delete: (factId: string) =>
        apiFetch<{ data: { success: boolean } }>(`/api/chat/facts/${factId}`, { method: 'DELETE' }),
      getSource: (factId: string) =>
        apiFetch<{ messageText: string | null; spans: SourceSpan[] }>(`/api/chat/facts/${factId}/source`),
    },
  },

  // Knowledge Graph
  kg: {
    health: () =>
      apiFetch<KgObservabilityDashboard>('/api/kg-sync/health'),
    scanAnomalies: () =>
      apiFetch<KgAnomalyScanResult>('/api/kg-sync/anomalies', { timeout: 120000 }),
    backfill: () =>
      apiFetch<KgBackfillResult>('/api/kg-sync/backfill', { method: 'POST', timeout: 300000 }),
    events: (params?: { limit?: number; offset?: number; eventType?: string; entityType?: string }) => {
      const query = new URLSearchParams()
      if (params?.limit) query.set('limit', String(params.limit))
      if (params?.offset) query.set('offset', String(params.offset))
      if (params?.eventType) query.set('eventType', params.eventType)
      if (params?.entityType) query.set('entityType', params.entityType)
      const qs = query.toString()
      return apiFetch<{ events: KgSyncEventRow[]; total: number }>(`/api/kg-sync/events${qs ? `?${qs}` : ''}`)
    },
    eventsSummary: () =>
      apiFetch<KgSyncEventSummary>('/api/kg-sync/events/summary'),
    timeline: (entityType: string, entityId: string) =>
      apiFetch<{ timeline: KgTimelineEntry[] }>(`/api/kg-sync/timeline/${entityType}/${entityId}`),
    changes: (entityType: string, entityId: string, params?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams()
      if (params?.limit) query.set('limit', String(params.limit))
      if (params?.offset) query.set('offset', String(params.offset))
      const qs = query.toString()
      return apiFetch<{ changes: KgChangeLogEntry[]; total: number }>(`/api/kg-sync/changes/${entityType}/${entityId}${qs ? `?${qs}` : ''}`)
    },
    recentChanges: (params?: { days?: number; limit?: number; entityType?: string }) => {
      const query = new URLSearchParams()
      if (params?.days) query.set('days', String(params.days))
      if (params?.limit) query.set('limit', String(params.limit))
      if (params?.entityType) query.set('entityType', params.entityType)
      const qs = query.toString()
      return apiFetch<{ changes: KgChangeLogEntry[] }>(`/api/kg-sync/changes/recent${qs ? `?${qs}` : ''}`)
    },
    browser: {
      subgraph: (scope: string, entityIds?: string[], depth?: number, nodeTypes?: string[]) => {
        const query = new URLSearchParams({ scope })
        if (entityIds && entityIds.length > 0) query.set('entityIds', entityIds.join(','))
        if (depth) query.set('depth', String(depth))
        if (nodeTypes && nodeTypes.length > 0) query.set('nodeTypes', nodeTypes.join(','))
        return apiFetch<KgBrowserSubgraphResponse>(`/api/kg-browser/subgraph?${query}`)
      },
      expand: (nodeId: string, depth?: number) =>
        apiFetch<KgBrowserSubgraphResponse>(`/api/kg-browser/expand/${nodeId}${depth ? `?depth=${depth}` : ''}`),
      search: (q: string, scope?: string, entityId?: string) => {
        const query = new URLSearchParams({ q })
        if (scope) query.set('scope', scope)
        if (entityId) query.set('entityId', entityId)
        return apiFetch<KgBrowserSearchResult[]>(`/api/kg-browser/search?${query}`)
      },
      nodeDetail: (nodeId: string) =>
        apiFetch<KgBrowserNodeDetail>(`/api/kg-browser/node/${nodeId}`),
      documentPreview: (documentId: string) =>
        apiFetch<KgDocumentPreview>(`/api/kg-browser/document/${documentId}/preview`),
    },
  },

  solutions: {
    save: (data: { title: string; content: string; tags?: string[]; features?: Record<string, unknown> }) =>
      apiFetch<{ success: boolean; solution: { id: string; title: string; created_at: string } }>('/api/solutions', {
        method: 'POST', body: data,
      }),

    kb: {
      list: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params)}` : ''
        return apiFetch<{ documents: KbDocSummary[]; total: number }>(`/api/solutions/kb${query}`)
      },
      get: (id: string) =>
        apiFetch<{ document: KbDocDetail }>(`/api/solutions/kb/${id}`).then(r => r.document),
      uploadFile: async (file: File, meta?: { title?: string; categoryId?: string; tags?: string; description?: string }) => {
        const formData = new FormData()
        formData.append('file', file)
        if (meta?.title) formData.append('title', meta.title)
        if (meta?.categoryId) formData.append('categoryId', meta.categoryId)
        if (meta?.tags) formData.append('tags', meta.tags)
        if (meta?.description) formData.append('description', meta.description)
        const response = await fetch(`${API_BASE_URL}/api/solutions/kb/upload`, {
          method: 'POST',
          body: formData,
        })
        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Upload failed' }))
          throw new Error(err.error || `HTTP ${response.status}`)
        }
        const data = await response.json()
        return data.document as KbDocDetail
      },
      createFromText: (data: { title: string; contentMd: string; categoryId?: string; tags?: string[]; description?: string }) =>
        apiFetch<{ document: KbDocDetail }>('/api/solutions/kb/text', { method: 'POST', body: data }).then(r => r.document),
      createFromSolution: (data: { title: string; contentMd: string; solutionId?: string; sessionId?: string; categoryId?: string; tags?: string[]; description?: string }) =>
        apiFetch<{ document: KbDocDetail }>('/api/solutions/kb/from-solution', { method: 'POST', body: data }).then(r => r.document),
      update: (id: string, data: { title?: string; description?: string; contentMd?: string; categoryId?: string | null; tags?: string[] }) =>
        apiFetch<{ document: KbDocDetail }>(`/api/solutions/kb/${id}`, { method: 'PUT', body: data }).then(r => r.document),
      delete: (id: string) =>
        apiFetch<{ success: boolean }>(`/api/solutions/kb/${id}`, { method: 'DELETE' }),
      download: async (id: string) => {
        const res = await fetch(`${API_BASE_URL}/api/solutions/kb/${id}/download`)
        if (!res.ok) throw new Error(`Download failed: ${res.statusText}`)
        return res.blob()
      },
      categories: {
        list: () =>
          apiFetch<{ categories: KbCategory[] }>('/api/solutions/kb/categories').then(r => r.categories),
        create: (data: { name: string; description?: string; color?: string }) =>
          apiFetch<{ category: KbCategory }>('/api/solutions/kb/categories', { method: 'POST', body: data }).then(r => r.category),
        update: (id: string, data: { name?: string; description?: string; color?: string; sortOrder?: number }) =>
          apiFetch<{ category: KbCategory }>(`/api/solutions/kb/categories/${id}`, { method: 'PUT', body: data }).then(r => r.category),
        delete: (id: string) =>
          apiFetch<{ success: boolean }>(`/api/solutions/kb/categories/${id}`, { method: 'DELETE' }),
      },
    },

    qa: {
      // Thread CRUD
      listThreads: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params)}` : ''
        return apiFetch<{ threads: QaThreadSummary[]; total: number }>(`/api/solutions/qa${query}`)
      },
      createThread: (data: { rawEmail: string; title?: string; senderName?: string; senderEmail?: string; customerId?: string; customerName?: string; category?: string; tags?: string[] }) =>
        apiFetch<{ thread: QaThreadSummary }>('/api/solutions/qa', { method: 'POST', body: data }).then(r => r.thread),
      getThread: (threadId: string) =>
        apiFetch<{ thread: QaThreadDetail }>(`/api/solutions/qa/${threadId}`).then(r => r.thread),
      updateThread: (threadId: string, data: { title?: string; status?: string; category?: string; tags?: string[] }) =>
        apiFetch<{ thread: QaThreadSummary }>(`/api/solutions/qa/${threadId}`, { method: 'PUT', body: data }).then(r => r.thread),
      archiveThread: (threadId: string) =>
        apiFetch<{ success: boolean }>(`/api/solutions/qa/${threadId}`, { method: 'DELETE' }),

      // AI Pipeline
      extractQuestions: (threadId: string) =>
        apiFetch<{ questions: QaQuestionDetail[] }>(`/api/solutions/qa/${threadId}/extract`, { method: 'POST' }).then(r => r.questions),
      matchQuestions: (threadId: string) =>
        apiFetch<{ matched: number; total: number }>(`/api/solutions/qa/${threadId}/match`, { method: 'POST' }),
      generateAll: (threadId: string, opts?: { tone?: string }) =>
        apiFetch<{ answers: QaAnswerDetail[] }>(`/api/solutions/qa/${threadId}/generate`, { method: 'POST', body: opts || {} }).then(r => r.answers),
      getResponse: (threadId: string, opts?: { format?: string; includeOriginal?: string; greeting?: string; closing?: string }) => {
        const params = new URLSearchParams()
        if (opts?.format) params.set('format', opts.format)
        if (opts?.includeOriginal) params.set('includeOriginal', opts.includeOriginal)
        if (opts?.greeting) params.set('greeting', opts.greeting)
        if (opts?.closing) params.set('closing', opts.closing)
        const query = params.toString() ? `?${params}` : ''
        return apiFetch<{ content: string; questionCount: number; answeredCount: number }>(`/api/solutions/qa/${threadId}/response${query}`)
      },

      // Review & Export
      reviewThread: (threadId: string) =>
        apiFetch<{ summary: Record<string, number>; threadStatus: string }>(`/api/solutions/qa/${threadId}/review`, { method: 'POST' }),
      exportToKb: (threadId: string, opts?: { questionIds?: string[]; mode?: string; categoryId?: string; tags?: string[] }) =>
        apiFetch<{ exported: number; documentIds: string[] }>(`/api/solutions/qa/${threadId}/export-to-kb`, { method: 'POST', body: opts || {} }),

      // Question Management
      listAllQuestions: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params)}` : ''
        return apiFetch<{ questions: QaQuestionDetail[]; total: number }>(`/api/solutions/qa/questions${query}`)
      },
      updateQuestion: (questionId: string, data: { questionText?: string; questionSummary?: string; category?: string; tags?: string[] }) =>
        apiFetch<{ question: QaQuestionDetail }>(`/api/solutions/qa/questions/${questionId}`, { method: 'PUT', body: data }).then(r => r.question),
      deleteQuestion: (questionId: string) =>
        apiFetch<{ success: boolean }>(`/api/solutions/qa/questions/${questionId}`, { method: 'DELETE' }),
      reviewQuestion: (questionId: string, action: 'approve' | 'reject' | 'skip') =>
        apiFetch<{ status: string }>(`/api/solutions/qa/questions/${questionId}/review`, { method: 'POST', body: { action } }),

      // Answer Ops
      generateAnswer: (questionId: string, opts?: { tone?: string; instructions?: string }) =>
        apiFetch<{ answer: QaAnswerDetail }>(`/api/solutions/qa/questions/${questionId}/generate`, { method: 'POST', body: opts || {} }).then(r => r.answer),
      regenerateAnswer: (questionId: string, opts?: { instructions?: string }) =>
        apiFetch<{ answer: QaAnswerDetail }>(`/api/solutions/qa/questions/${questionId}/regenerate`, { method: 'POST', body: opts || {} }).then(r => r.answer),
      createManualAnswer: (questionId: string, answerMd: string) =>
        apiFetch<{ answer: QaAnswerDetail }>(`/api/solutions/qa/questions/${questionId}/answer`, { method: 'POST', body: { answerMd } }).then(r => r.answer),
      selectAnswer: (questionId: string, answerId: string) =>
        apiFetch<{ success: boolean }>(`/api/solutions/qa/questions/${questionId}/select`, { method: 'PUT', body: { answerId } }),
      findSimilar: (questionId: string) =>
        apiFetch<{ matches: QaMatchResult[] }>(`/api/solutions/qa/questions/${questionId}/similar`).then(r => r.matches),
      deleteAnswer: (questionId: string, answerId: string) =>
        apiFetch<{ success: boolean }>(`/api/solutions/qa/questions/${questionId}/answers/${answerId}`, { method: 'DELETE' }),

      // Search
      search: (q: string, params?: { limit?: string; offset?: string }) => {
        const sp = new URLSearchParams({ q })
        if (params?.limit) sp.set('limit', params.limit)
        if (params?.offset) sp.set('offset', params.offset)
        return apiFetch<{ questions: QaQuestionDetail[]; total: number }>(`/api/solutions/qa/search?${sp}`)
      },
    },
  },

  // LLM Council
  council: {
    sessions: {
      list: (customerId?: string) => {
        const query = customerId ? `?customerId=${customerId}` : ''
        return apiFetch<{ sessions: CouncilSessionSummary[]; total: number }>(`/api/council/sessions${query}`)
      },
      get: (id: string) =>
        apiFetch<{ session: CouncilSessionDetail }>(`/api/council/sessions/${id}`).then(r => r.session),
      delete: (id: string) =>
        apiFetch<{ success: boolean }>(`/api/council/sessions/${id}`, { method: 'DELETE' }),
    },
    submit: (data: {
      documentText: string
      documentTitle?: string
      customerId?: string
      sourceArtifactType?: string
      sourceArtifactId?: string
      qualityTier: CouncilQualityTier
    }) =>
      apiFetch<{ session: CouncilSessionDetail }>('/api/council/submit', {
        method: 'POST',
        body: data,
        timeout: 300000,
      }).then(r => r.session),
    adopt: (sessionId: string) =>
      apiFetch<{ success: boolean; artifactType: string; artifactId: string }>(
        `/api/council/sessions/${sessionId}/adopt`,
        { method: 'POST' }
      ),
    extractText: async (file: File): Promise<{ text: string; filename: string; mimeType: string }> => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE_URL}/api/council/extract-text`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(err.message || err.error || 'Failed to extract text')
      }
      return res.json()
    },
    download: async (sessionId: string, format: 'md' | 'txt' | 'docx' | 'pdf'): Promise<void> => {
      const res = await fetch(`${API_BASE_URL}/api/council/sessions/${sessionId}/download?format=${format}`)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Download failed' }))
        throw new Error(err.message || err.error || 'Download failed')
      }
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') || ''
      const filenameMatch = disposition.match(/filename="(.+?)"/)
      const filename = filenameMatch?.[1] ?? `council-revision.${format}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },

  // Document Studio
  studio: {
    templates: () =>
      apiFetch<{ templates: StudioTemplateInfo[] }>('/api/studio/templates'),
    sessions: {
      list: (limit?: number) => {
        const query = limit ? `?limit=${limit}` : ''
        return apiFetch<{ sessions: StudioSessionSummary[]; total: number }>(`/api/studio/sessions${query}`)
      },
      get: (id: string) =>
        apiFetch<{ session: StudioSessionDetail }>(`/api/studio/sessions/${id}`).then(r => r.session),
      create: (data: {
        inputText: string
        inputTitle?: string
        inputFormat?: 'markdown' | 'text' | 'html'
        sourceType?: string
        sourceId?: string
        template?: StudioTemplate
      }) =>
        apiFetch<{ session: StudioSessionDetail }>('/api/studio/sessions', {
          method: 'POST',
          body: data,
        }).then(r => r.session),
      delete: (id: string) =>
        apiFetch<{ success: boolean }>(`/api/studio/sessions/${id}`, { method: 'DELETE' }),
    },
    beautify: (sessionId: string, template?: StudioTemplate) =>
      apiFetch<{ session: StudioSessionDetail }>(`/api/studio/sessions/${sessionId}/beautify`, {
        method: 'POST',
        body: { template },
        timeout: 300000,
      }).then(r => r.session),
    status: (sessionId: string) =>
      apiFetch<{ status: string; progress: StudioProgress }>(`/api/studio/sessions/${sessionId}/status`),
    export: async (sessionId: string, format: 'md' | 'docx' | 'pdf' | 'epub'): Promise<void> => {
      const res = await fetch(`${API_BASE_URL}/api/studio/sessions/${sessionId}/export?format=${format}`)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Export failed' }))
        throw new Error(err.message || err.error || 'Export failed')
      }
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') || ''
      const filenameMatch = disposition.match(/filename="(.+?)"/)
      const filename = filenameMatch?.[1] ?? `studio-export.${format}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    updateViz: (sessionId: string, vizId: string, updates: { imagePrompt?: string; selected?: boolean }) =>
      apiFetch<{ visualization: StudioVisualization }>(`/api/studio/sessions/${sessionId}/viz/${vizId}`, {
        method: 'PUT',
        body: updates,
      }).then(r => r.visualization),
    generateViz: (sessionId: string, vizId: string) =>
      apiFetch<{ visualization: StudioVisualization }>(`/api/studio/sessions/${sessionId}/viz/${vizId}/generate`, {
        method: 'POST',
        timeout: 60000,
      }).then(r => r.visualization),
    assemble: (sessionId: string) =>
      apiFetch<{ session: StudioSessionDetail }>(`/api/studio/sessions/${sessionId}/assemble`, {
        method: 'POST',
        timeout: 120000,
      }).then(r => r.session),
    vizPngUrl: (sessionId: string, vizId: string) =>
      `${API_BASE_URL}/api/studio/sessions/${sessionId}/viz/${vizId}.png`,
    generateCustomImage: (sessionId: string, prompt: string) =>
      apiFetch<{ visualization: StudioVisualization }>(`/api/studio/sessions/${sessionId}/custom-image`, {
        method: 'POST',
        body: { prompt },
        timeout: 60000,
      }).then(r => r.visualization),
    importFromSolution: (solutionId: string) =>
      apiFetch<{ text: string; title: string }>(`/api/studio/import/solution/${solutionId}`, {
        method: 'POST',
      }),
    importFromEditorSession: (sessionId: string) =>
      apiFetch<{ text: string; title: string }>(`/api/studio/import/editor/${sessionId}`, {
        method: 'POST',
      }),
    extractText: async (file: File): Promise<{ text: string; filename: string; mimeType: string }> => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE_URL}/api/studio/extract-text`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(err.message || err.error || 'Failed to extract text')
      }
      return res.json()
    },
  },

  // Global Search
  search: {
    global: (q: string, options?: { limit?: number; rerank?: boolean }) => {
      const params = new URLSearchParams({ q })
      if (options?.limit) params.set('limit', String(options.limit))
      if (options?.rerank === false) params.set('rerank', 'false')
      return apiFetch<GlobalSearchResponse>(`/api/search?${params}`)
    },
  },

  // ================================================================
  // Editor
  // ================================================================
  editor: {
    sessions: {
      list: (limit?: number) => {
        const query = limit ? `?limit=${limit}` : ''
        return apiFetch<{ sessions: EditorSessionSummary[]; total: number }>(`/api/editor/sessions${query}`)
      },
      get: (id: string) =>
        apiFetch<{ session: EditorSessionDetail }>(`/api/editor/sessions/${id}`).then(r => r.session),
      create: (data: {
        documentText: string
        documentTitle?: string
        sourceSolutionId?: string
        qualityTier?: EditorQualityTier
      }) =>
        apiFetch<{ session: EditorSessionDetail }>('/api/editor/sessions', {
          method: 'POST',
          body: data,
        }).then(r => r.session),
      delete: (id: string) =>
        apiFetch<{ success: boolean }>(`/api/editor/sessions/${id}`, { method: 'DELETE' }),
    },
    run: (sessionId: string, editorType: EditorTypeKey, qualityTier?: EditorQualityTier) =>
      apiFetch<{ result: EditorResultData }>(`/api/editor/sessions/${sessionId}/run`, {
        method: 'POST',
        body: { editorType, qualityTier },
        timeout: 180000,
      }).then(r => r.result),
    apply: (sessionId: string, editorType: EditorTypeKey, indices: number[]) =>
      apiFetch<{ currentText: string; version: number }>(`/api/editor/sessions/${sessionId}/apply`, {
        method: 'POST',
        body: { editorType, indices },
      }),
    adopt: (sessionId: string, editorType: EditorTypeKey) =>
      apiFetch<{ currentText: string; version: number }>(`/api/editor/sessions/${sessionId}/adopt`, {
        method: 'POST',
        body: { editorType },
      }),
    rollback: (sessionId: string, versionIndex: number) =>
      apiFetch<{ currentText: string; version: number }>(`/api/editor/sessions/${sessionId}/rollback`, {
        method: 'POST',
        body: { versionIndex },
      }),
    snapshot: (sessionId: string, label?: string) =>
      apiFetch<{ version: number }>(`/api/editor/sessions/${sessionId}/snapshot`, {
        method: 'POST',
        body: { label },
      }),
    updateMetadata: (sessionId: string, metadata: Record<string, unknown>) =>
      apiFetch<{ success: boolean }>(`/api/editor/sessions/${sessionId}/metadata`, {
        method: 'PUT',
        body: metadata,
      }),
    extractText: async (file: File): Promise<{ text: string; filename: string; mimeType: string }> => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE_URL}/api/editor/extract-text`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(err.message || err.error || 'Failed to extract text')
      }
      return res.json()
    },
    export: async (sessionId: string, format: 'md' | 'docx' | 'pdf' | 'epub'): Promise<void> => {
      const res = await fetch(`${API_BASE_URL}/api/editor/sessions/${sessionId}/export?format=${format}`)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Export failed' }))
        throw new Error(err.message || err.error || 'Export failed')
      }
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') || ''
      const filenameMatch = disposition.match(/filename="(.+?)"/)
      const filename = filenameMatch?.[1] ?? `editor-export.${format}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },

  // Briefings
  briefing: {
    list: () =>
      apiFetch<{
        briefings: Array<{
          id: string
          filename: string
          title: string
          itemCount: number
          actionCount: number
          createdAt: string | null
        }>
      }>('/api/briefing/list'),

    getPunchList: () =>
      apiFetch<{
        punchlist: Array<BriefingActionItem & { briefingId: string; tab: string }>
        wplinks: Array<BriefingActionItem & { briefingId: string; tab: string }>
        ideas: Array<BriefingActionItem & { briefingId: string; tab: string }>
        total: number
      }>('/api/briefing/punchlist'),

    getHtml: (id: string) =>
      fetch(`${API_BASE_URL}/api/briefing/${id}/html`).then((r) => {
        if (!r.ok) throw new Error(`Briefing ${id} not found`)
        return r.text()
      }),

    getActions: (id: string) =>
      apiFetch<{
        briefingId: string
        actions: BriefingActions
        total: number
      }>(`/api/briefing/${id}/actions`),

    saveActions: (id: string, actions: BriefingActions) =>
      apiFetch<{ success: boolean; total: number }>(`/api/briefing/${id}/actions`, {
        method: 'POST',
        body: actions,
      }),

    deleteAction: (id: string, tab: string, index: number) =>
      apiFetch<{ success: boolean; remaining: number }>(
        `/api/briefing/${id}/actions/${tab}/${index}`,
        { method: 'DELETE' }
      ),

    syncToTracker: (id: string) =>
      apiFetch<{
        success: boolean
        results: Array<{ type: string; id: string; status: string; message: string }>
        syncedAt: string
      }>(`/api/briefing/${id}/actions/sync`, { method: 'POST' }),

    runImpactAnalysis: (data: {
      headline: string
      summary: string
      sourceUrl?: string
      sourceName?: string
      date?: string
      category?: string
      paperIds?: string[]
    }) =>
      apiFetch<{
        success: boolean
        data: {
          headline: string
          strategicValue: string
          valueVerdict: 'High Value' | 'Moderate Value' | 'Low Value' | 'Not Relevant'
          paperAnalyses: Array<{
            paperId: string
            paperTitle: string
            priority: string
            valueToUs: string
            alignment: string[]
            gaps: string[]
            updateSuggestions: string[]
          }>
          ebookImpacts: Array<{
            ebookId: string
            ebookTitle: string
            relevant: boolean
            assessment: string
          }>
          newPaperRecommendation: {
            recommended: boolean
            title: string | null
            rationale: string | null
            suggestedCluster: string | null
          }
          overallAssessment: string
          crossReferences: string[]
          model: string
          usage: { promptTokens: number; completionTokens: number; totalTokens: number }
        }
      }>('/api/briefing/impact-analysis', {
        method: 'POST',
        body: data,
        timeout: 120000,
      }),

    generate: (options?: { period?: 'am' | 'pm'; date?: string; categories?: number[] }) =>
      apiFetch<{
        success: boolean
        briefingId: string
        stats: { total: number; high: number; medium: number; low: number }
        files: { md: string; html: string }
      }>('/api/briefing/generate', {
        method: 'POST',
        body: options || {},
        timeout: 300000,
      }),

    analyzeUrl: (url: string) =>
      apiFetch<{
        success: boolean
        data: {
          headline: string
          summary: string
          sourceUrl: string
          sourceName: string
          suggestedPapers: string[]
          category: string
        }
      }>('/api/briefing/analyze-url', {
        method: 'POST',
        body: { url },
        timeout: 60000,
      }),
  },

  // Task Board
  tasks: {
    list: (filters?: Record<string, string>) => {
      const params = new URLSearchParams(filters || {}).toString()
      return apiFetch<TaskListResponse>(`/api/tasks${params ? `?${params}` : ''}`)
    },
    get: (id: string) => apiFetch<Task & { comments: TaskComment[] }>(`/api/tasks/${id}`),
    create: (input: Partial<Task>) =>
      apiFetch<Task>('/api/tasks', { method: 'POST', body: input }),
    update: (id: string, input: Partial<Task>) =>
      apiFetch<Task>(`/api/tasks/${id}`, { method: 'PUT', body: input }),
    patch: (id: string, input: Partial<Task>) =>
      apiFetch<Task>(`/api/tasks/${id}`, { method: 'PATCH', body: input }),
    delete: (id: string) =>
      apiFetch<{ success: boolean }>(`/api/tasks/${id}`, { method: 'DELETE' }),
    addComment: (taskId: string, author: string, content: string) =>
      apiFetch<TaskComment>(`/api/tasks/${taskId}/comments`, {
        method: 'POST',
        body: { author, content },
      }),
    getComments: (taskId: string) =>
      apiFetch<TaskComment[]>(`/api/tasks/${taskId}/comments`),
    stats: () => apiFetch<TaskStats>('/api/tasks/stats'),
    importBriefing: (briefingId: string) =>
      apiFetch<TaskImportResult>(`/api/tasks/import/briefing/${briefingId}`, { method: 'POST' }),
    importAll: () =>
      apiFetch<{ results: TaskImportResult[]; totalImported: number; totalSkipped: number }>(
        '/api/tasks/import/briefings',
        { method: 'POST' }
      ),
    bulkStatus: (ids: string[], status: string) =>
      apiFetch<{ updated: number }>('/api/tasks/bulk-status', {
        method: 'POST',
        body: { ids, status },
      }),
  },

  // Tech Evaluations
  techEvals: {
    list: (filters?: Record<string, string>) => {
      const params = new URLSearchParams(filters || {}).toString()
      return apiFetch<TechEvalListResponse>(`/api/tech-evals${params ? `?${params}` : ''}`)
    },
    get: (id: string) => apiFetch<TechEval>(`/api/tech-evals/${id}`),
    create: (input: Partial<TechEval>) =>
      apiFetch<TechEval>('/api/tech-evals', { method: 'POST', body: input }),
    update: (id: string, input: Partial<TechEval>) =>
      apiFetch<TechEval>(`/api/tech-evals/${id}`, { method: 'PUT', body: input }),
    patch: (id: string, input: Partial<TechEval>) =>
      apiFetch<TechEval>(`/api/tech-evals/${id}`, { method: 'PATCH', body: input }),
    delete: (id: string) =>
      apiFetch<{ success: boolean }>(`/api/tech-evals/${id}`, { method: 'DELETE' }),
    stats: () => apiFetch<TechEvalStats>('/api/tech-evals/stats'),
    importTracker: () =>
      apiFetch<TechEvalImportResult>('/api/tech-evals/import/tracker', { method: 'POST' }),
    importFiles: () =>
      apiFetch<TechEvalImportResult>('/api/tech-evals/import/files', { method: 'POST' }),
    importAll: () =>
      apiFetch<{ tracker: TechEvalImportResult; files: TechEvalImportResult }>(
        '/api/tech-evals/import/all',
        { method: 'POST' }
      ),
    upsert: (input: Partial<TechEval>) =>
      apiFetch<TechEval>('/api/tech-evals/upsert', { method: 'POST', body: input }),
    nextId: () => apiFetch<{ nextId: string }>('/api/tech-evals/next-id'),
  },

  // AI Ops (Throttle, Budget, Forecast)
  health: {
    throttle: () =>
      apiFetch<ThrottleSnapshot>('/api/health/throttle'),
    setThrottle: (value: number) =>
      apiFetch<ThrottleSnapshot>('/api/health/throttle', { method: 'PATCH', body: { value } }),
    budgets: () =>
      apiFetch<BudgetSnapshot>('/api/health/budgets'),
    setBudgets: (data: { capUsd?: number; enforcementMode?: string; alertThresholds?: number[] }) =>
      apiFetch<BudgetSnapshot>('/api/health/budgets', { method: 'PATCH', body: data }),
    forecast: () =>
      apiFetch<CostForecast>('/api/health/cost-forecast'),
    tokenUsage: (days?: number) =>
      apiFetch<TokenUsageSummary>(`/api/health/token-usage?days=${days ?? 7}`),
    events: (limit?: number) =>
      apiFetch<{ events: AiOpsEvent[] }>(`/api/health/throttle/events${limit ? `?limit=${limit}` : ''}`),
    routing: () =>
      apiFetch<RoutingState>('/api/health/routing'),
  },

  // SAFE Review
  safeReview: {
    run: (content: string, title?: string) =>
      apiFetch<SafeReviewResult>('/api/safe-review', { method: 'POST', body: { content, title }, timeout: 120000 }),
    list: () =>
      apiFetch<SafeReviewListItem[]>('/api/safe-review'),
    get: (id: string) =>
      apiFetch<SafeReviewResult>(`/api/safe-review/${id}`),
    markdownUrl: (id: string) =>
      `${API_BASE_URL}/api/safe-review/${id}/markdown`,
  },

  consciousness: {
    get: (days?: number) =>
      apiFetch(`/api/consciousness${days ? `?days=${days}` : ''}`),
  },

}

// ============================================================================
// Task Types
// ============================================================================

export type TaskCategory = 'punch-list' | 'feature' | 'bug' | 'content' | 'infrastructure' | 'research'
export type TaskStatus = 'open' | 'in-progress' | 'blocked' | 'done' | 'deferred'
export type TaskPriority = 'critical' | 'high' | 'medium' | 'low'

export interface Task {
  id: string
  title: string
  description: string | null
  category: TaskCategory
  status: TaskStatus
  priority: TaskPriority
  linked_document_ids: string[]
  source_type: string | null
  source_ref: string | null
  source_url: string | null
  source_name: string | null
  tags: string[]
  assigned_to: string | null
  due_date: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export interface TaskComment {
  id: string
  task_id: string
  author: 'claude' | 'user' | 'system'
  content: string
  created_at: string
}

export interface TaskStats {
  byStatus: Record<string, number>
  byCategory: Record<string, number>
  byPriority: Record<string, number>
  total: number
  overdue: number
  recentlyCompleted: number
}

export interface TaskListResponse {
  tasks: Task[]
  total: number
  page: number
  limit: number
}

export interface TaskImportResult {
  imported: number
  skipped: number
  briefingId: string
}

// ============================================================================
// Tech Evaluation Types
// ============================================================================

export type TechEvalVerdict = 'ADOPT' | 'TRIAL' | 'ASSESS' | 'HOLD' | 'REJECT'
export type TechEvalStatus = 'queued' | 'in-progress' | 'completed' | 'failed'

export interface TechEvalScores {
  r?: number
  d?: number
  b?: number
  w?: number
  tech?: string
}

export interface TechEval {
  id: string
  eval_id: string
  name: string
  slug: string
  verdict: TechEvalVerdict
  category: string
  status: TechEvalStatus
  wave: number | null
  duration: string | null
  artifacts: string[]
  tags: string[]
  scores: TechEvalScores | null
  eval_url: string | null
  notes: string | null
  evaluated_at: string | null
  created_at: string
  updated_at: string
}

export interface TechEvalStats {
  byVerdict: Record<string, number>
  byCategory: Record<string, number>
  byStatus: Record<string, number>
  total: number
  adopted: number
  recentlyEvaluated: number
}

export interface TechEvalListResponse {
  evaluations: TechEval[]
  total: number
  page: number
  limit: number
}

export interface TechEvalImportResult {
  imported: number
  skipped: number
  errors: string[]
}

// ============================================================================
// Briefing Types
// ============================================================================

export interface BriefingActionItem {
  headline: string
  summary: string
  badge: string
  date: string
  sourceUrl: string
  sourceName: string
  papers: string[]
  theme: string
  category: string
  addedAt: string
}

export interface BriefingActions {
  punchlist: BriefingActionItem[]
  wplinks: BriefingActionItem[]
  ideas: BriefingActionItem[]
  lastSynced?: string
}

// ============================================================================
// Global Search Types
// ============================================================================

export interface GlobalSearchResult {
  id: string
  type: 'customer' | 'opportunity' | 'document' | 'kg_node' | 'knowledge_chunk' | 'stakeholder'
  title: string
  snippet: string
  confidence: number
  relevanceNote: string
  url: string
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[]
  query: string
  candidateCount: number
  reranked: boolean
  durationMs: number
  cached: boolean
}

// ============================================================================
// Document Studio Types
// ============================================================================

export type StudioTemplate = 'professional_report' | 'executive_brief' | 'technical_document' | 'proposal' | 'blog' | 'white_paper' | 'ebook' | 'email'

export interface StudioTemplateInfo {
  id: string
  name: string
  description: string
  icon: string
  coverPage: boolean
  toc: boolean
  maxCharts: number
  promptModifier: string
}

export interface StudioSessionSummary {
  id: string
  inputTitle: string | null
  inputWordCount: number | null
  template: string | null
  pipelineStatus: string
  createdAt: string
}

export interface StudioSessionDetail {
  id: string
  inputTitle: string | null
  inputText: string
  inputFormat: string
  inputWordCount: number | null
  sourceType: string | null
  sourceId: string | null
  template: string | null
  pipelineStatus: string
  pipelineProgress: StudioProgress
  structureAnalysis: StudioStructureAnalysis | null
  beautifiedText: string | null
  beautifiedHtml: string | null
  visualizations: StudioVisualization[]
  generationMetadata: unknown | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

export interface StudioProgress {
  stage?: string
  percent?: number
  message?: string
}

export interface StudioStructureAnalysis {
  sections: Array<{
    title: string
    startLine: number
    endLine: number
    type: string
    quality: string
  }>
  dataPatterns: Array<{
    location: string
    description: string
    chartType: string
    dataPoints: string
  }>
  keyMetrics: Array<{
    label: string
    value: string
    context: string
  }>
  overallAssessment: string
}

export type StudioVizType = 'mermaid' | 'chart' | 'infographic'
export type StudioVizStatus = 'suggested' | 'generating' | 'generated' | 'failed'

export interface StudioVisualization {
  id: string
  type: StudioVizType
  title: string
  description: string
  code: string
  imagePrompt: string
  pngBase64?: string
  status: StudioVizStatus
  selected: boolean
}

// ============================================================================
// Q&A Engine Types
// ============================================================================

export interface QaThreadSummary {
  id: string
  title: string | null
  sender_name: string | null
  sender_email: string | null
  customer_id: string | null
  customer_name: string | null
  question_count: number
  status: string
  category: string | null
  tags: string[]
  is_archived: boolean
  created_at: string
  updated_at: string
}

export interface QaThreadDetail extends QaThreadSummary {
  raw_email: string
  generation_metadata: Record<string, unknown> | null
  questions: QaQuestionDetail[]
}

export interface QaQuestionDetail {
  id: string
  thread_id: string
  question_index: number
  question_text: string
  question_summary: string | null
  category: string | null
  complexity: string
  status: string
  current_answer_version: number
  best_match_question_id: string | null
  best_match_score: number | null
  tags: string[]
  created_at: string
  updated_at: string
  answers?: QaAnswerDetail[]
  match?: QaMatchResult | null
}

export interface QaAnswerDetail {
  id: string
  question_id: string
  version: number
  answer_md: string
  source: string
  source_question_id: string | null
  confidence_score: number | null
  generation_metadata: Record<string, unknown> | null
  is_selected: boolean
  created_at: string
}

export interface QaMatchResult {
  question_id: string
  question_text: string
  question_summary: string | null
  similarity: number
  thread_title: string | null
  answer_text: string | null
}

// ============================================================================
// Knowledge Base Types
// ============================================================================

export interface KbCategory {
  id: string
  name: string
  slug: string
  description: string | null
  color: string
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface KbDocSummary {
  id: string
  title: string
  description: string | null
  category_id: string | null
  category_name: string | null
  category_color: string | null
  tags: string[]
  source_type: 'upload' | 'paste' | 'solution_output'
  filename: string | null
  file_size_bytes: number | null
  mime_type: string | null
  is_archived: boolean
  created_at: string
  updated_at: string
}

export interface KbDocDetail extends KbDocSummary {
  content_md: string | null
  file_path: string | null
  content_hash: string | null
  source_solution_id: string | null
  source_session_id: string | null
}

// ============================================================================
// Chat Types
// ============================================================================

export interface ChatSessionResponse {
  id: string
  title: string | null
  scope: string
  entity_type: string | null
  entity_id: string | null
  entity_name: string | null
  message_count: number
  total_tokens: number
  last_message_at: string | null
  is_archived: boolean
  handoff: any
  use_memory: boolean
  created_at: string
  updated_at: string
}

export interface ChatMessageResponse {
  id: string
  session_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  context_scope: string | null
  tokens_used: number | null
  model_used: string | null
  tool_calls: any
  document_refs: any
  created_at: string
}

export interface ChatKnowledgeDocResponse {
  id: string
  title: string
  doc_type: string
  content: string
  extracted_summary: string | null
  tags: string[]
  source_url: string | null
  version_info: string | null
  is_active: boolean
  embedding_status?: string
  chunk_count?: number
  created_at: string
  updated_at: string
}

// ============================================================================
// Source Grounding Types
// ============================================================================

export interface SourceSpan {
  start: number;
  end: number;
  excerpt: string;
}

export interface SourceGroundingEntry {
  field: string;
  start: number;
  end: number;
  excerpt: string;
}

// ============================================================================
// Settings / API Key Types
// ============================================================================

export type ApiProvider = 'anthropic' | 'openai' | 'google' | 'perplexity' | 'fireflies' | 'mistral' | 'sam_gov'

export interface ApiKeyStatus {
  provider: ApiProvider
  hasCustomKey: boolean
  useCustom: boolean
  hasEnvKey: boolean
  source: 'custom' | 'environment' | 'none'
  lastUpdated: string | null
}

export interface ApiKeySettings {
  userId: string
  keys: ApiKeyStatus[]
  updatedAt: string | null
}

export interface ApiKeyValidationResult {
  valid: boolean
  error?: string
}

export interface ScoringWeights {
  titleKeywords: Record<string, number>
  orgType: Record<string, number>
  statePriority: Record<string, number>
  sourceQuality: Record<string, number>
}

// ============================================================================
// Kaiju Images Types
// ============================================================================

export interface KaijuImage {
  filename: string
  path: string
  url: string
  size: number
  uploadedAt: string
}

export interface KaijuImagesResponse {
  images: KaijuImage[]
}

export interface KaijuImageUploadResponse extends KaijuImage {}

export const KAIJU_IMAGE_SPECS = {
  minWidth: 1280,
  minHeight: 720,
  idealWidth: 1920,
  idealHeight: 1080,
  maxWidth: 2560,
  maxHeight: 1440,
  maxFileSize: 500 * 1024, // 500KB
  allowedFormats: ['image/jpeg', 'image/png'] as const,
  allowedExtensions: ['.jpg', '.jpeg', '.png'] as const,
}

// ============================================================================
// Agent Mode Types
// ============================================================================

export type AgentModeFeature = 'chat' | 'council' | 'kgContext' | 'briefing'

export interface AgentModeSettings {
  chat: boolean
  council: boolean
  kgContext: boolean
  briefing: boolean
}

// ============================================================================
// NotebookLM Auth Types
// ============================================================================

export interface NotebookLmStatus {
  authenticated: boolean
  cookieAge: string
  lastExtracted: string
  cookieCount?: number
  error?: string
}

// ============================================================================
// Knowledge Graph Types
// ============================================================================

export interface KgConnectionHealth {
  connected: boolean
  serverVersion: string | null
  address: string | null
  latencyMs: number
}

export interface KgNodeRelationshipCounts {
  nodes: { label: string; count: number }[]
  relationships: { type: string; count: number }[]
}

export interface KgSyncLagMetric {
  entityType: string
  lastPgUpdate: string | null
  lastKgSync: string | null
  lagSeconds: number | null
  staleCount: number
}

export interface KgSyncEventSummary {
  period: string
  byEventType: { event_type: string; total: number; success: number; failure: number }[]
  byEntityType: { entity_type: string; total: number; avgDurationMs: number }[]
}

export interface KgObservabilityDashboard {
  connection: KgConnectionHealth
  nodeCounts: KgNodeRelationshipCounts
  syncLag: KgSyncLagMetric[]
  eventSummary: KgSyncEventSummary
}

export interface KgAnomalyResult {
  type: 'orphaned_node' | 'missing_relationship' | 'stale_sync' | 'duplicate_relationship' | 'disconnected_subgraph'
  severity: 'critical' | 'warning' | 'info'
  entityType: string
  entityId?: string
  entityName?: string
  description: string
  suggestedFix?: string
}

export interface KgAnomalyScanResult {
  scannedAt: string
  durationMs: number
  anomalies: KgAnomalyResult[]
  summary: { total: number; critical: number; warning: number; info: number }
}

export interface KgBackfillResult {
  success: boolean
  stats: {
    customers: number
    opportunities: number
    aes: number
    territories: number
    assignments: number
    competitors?: number
    teamMembers?: number
    stakeholders?: number
    stakeholderRelationships?: number
    meetings?: number
    meetingNotes?: number
    commandPlans?: number
    meddpicc?: number
    dapStrategies?: number
    tcps?: number
    valueStreams?: number
    cspPlans?: number
    battleCards?: number
    crossSell?: number
    coachingInsights?: number
    personalIntel?: number
    documents?: number
    missionDetails?: number
  }
  message: string
}

export interface KgSyncEventRow {
  id: string
  event_type: string
  entity_type: string
  entity_id: string | null
  status: string
  node_count: number
  relationship_count: number
  duration_ms: number | null
  error_message: string | null
  metadata: unknown | null
  created_at: string
}

export interface KgTimelineEntry {
  id: string
  changeType: string
  fieldName: string | null
  oldValue: string | null
  newValue: string | null
  changedBy: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  description: string
}

export interface KgChangeLogEntry {
  id: string
  entityType: string
  entityId: string
  changeType: string
  fieldName: string | null
  oldValue: string | null
  newValue: string | null
  changedBy: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

// KG Browser Types
export interface KgBrowserNode {
  id: string
  label: string
  caption: string
  properties: Record<string, unknown>
}

export interface KgBrowserRelationship {
  id: string
  from: string
  to: string
  type: string
  properties?: Record<string, unknown>
}

export interface KgBrowserSubgraphResponse {
  nodes: KgBrowserNode[]
  relationships: KgBrowserRelationship[]
  center?: string
  totalNodeCount: number
}

export interface KgBrowserSearchResult {
  id: string
  label: string
  caption: string
  score: number
}

export interface KgBrowserNodeDetail {
  id: string
  label: string
  caption: string
  properties: Record<string, unknown>
  relationships: { type: string; direction: 'in' | 'out'; targetId: string; targetCaption: string }[]
  detailUrl: string | null
}

export interface KgDocumentPreview {
  id: string
  customerId: string
  filename: string | null
  docType: string
  processingStatus: string
  preview: string | null
  hasMore: boolean
}

// ============================================================================
// LLM Council Types
// ============================================================================

export type CouncilQualityTier = 'highest' | 'high' | 'balanced' | 'economy'

export interface CouncilSessionSummary {
  id: string
  customerName: string | null
  documentTitle: string | null
  sourceArtifactType: string | null
  status: string
  qualityTier: string
  consensusScore: number | null
  adoptedAt: string | null
  createdAt: string
}

export interface CouncilSessionDetail extends CouncilSessionSummary {
  documentText: string
  customerId: string | null
  sourceArtifactId: string | null
  round1Reviews: CouncilPanelistReview[] | null
  round1Status: string
  round2Reviews: CouncilPanelistResponse[] | null
  round2Status: string
  round3Synthesis: CouncilModeratorSynthesis | null
  round3Status: string
  revisedDocument: string | null
  generationMetadata: unknown | null
}

export interface CouncilPanelistReview {
  persona: string
  displayName: string
  review: string
  score: number
  keyPoints: string[]
  timestamp: string
}

export interface CouncilPanelistResponse {
  persona: string
  displayName: string
  response: string
  agreements: string[]
  disagreements: string[]
  revisedScore: number
  timestamp: string
}

export interface CouncilModeratorSynthesis {
  recommendations: string[]
  revisedDocument: string
  consensusScore: number
  keyDebatePoints: Array<{
    topic: string
    positions: Array<{ persona: string; position: string }>
    resolution: string
  }>
  unanimousStrengths: string[]
  unanimousWeaknesses: string[]
}

// ============================================================================
// Editor Types
// ============================================================================

export type EditorQualityTier = 'highest' | 'high' | 'balanced' | 'economy'

export type EditorTypeKey =
  | 'development'
  | 'line'
  | 'grammar'
  | 'blind'
  | 'polish'
  | 'humanizer'
  | 'format'

export interface EditorSuggestionData {
  location: string
  original: string
  replacement: string
  reason: string
  severity: 'critical' | 'major' | 'minor' | 'style'
}

export interface EditorResultData {
  suggestions: EditorSuggestionData[]
  editedText: string
  score: number
  summary: string
  ebookMetadata?: {
    title?: string
    subtitle?: string
    author?: string
    language?: string
    suggestedGenre?: string
  }
  runCount: number
  lastRunAt: string
  model: string
  inputTokens: number
  outputTokens: number
  durationMs: number
  acceptedAt?: string
  acceptedCount?: number
}

export interface EditorVersionEntry {
  version: number
  source: string
  text: string
  label: string
  savedAt: string
}

export interface EditorSessionSummary {
  id: string
  documentTitle: string | null
  documentWordCount: number | null
  qualityTier: string
  status: string
  editorsRun: string[]
  createdAt: string
  updatedAt: string
}

export interface EditorSessionDetail {
  id: string
  documentTitle: string | null
  documentText: string
  documentWordCount: number | null
  sourceSolutionId: string | null
  qualityTier: string
  editorResults: Record<string, EditorResultData>
  currentText: string | null
  versionHistory: EditorVersionEntry[]
  ebookMetadata: unknown | null
  status: string
  generationMetadata: unknown | null
  createdAt: string
  updatedAt: string
}

export const EDITOR_PERSONAS: Array<{
  key: EditorTypeKey
  displayName: string
  emoji: string
  description: string
  suggestedOrder: number
}> = [
  { key: 'development', displayName: 'Development Editor', emoji: '\uD83D\uDCD0', description: 'Structure, pacing, argument flow, chapter organization, narrative arc', suggestedOrder: 1 },
  { key: 'line', displayName: 'Line Editor', emoji: '\u270D\uFE0F', description: 'Sentence clarity, flow, voice consistency, word choice, rhythm', suggestedOrder: 2 },
  { key: 'grammar', displayName: 'Grammar Editor', emoji: '\uD83D\uDCDD', description: 'Grammar, spelling, punctuation, syntax corrections only', suggestedOrder: 3 },
  { key: 'blind', displayName: 'Blind Editor', emoji: '\uD83E\uDEE3', description: 'Fresh-eyes reader review \u2014 confusion points, unclear references, assumptions', suggestedOrder: 4 },
  { key: 'polish', displayName: 'Polish Editor', emoji: '\u2728', description: 'Final readability pass \u2014 tone refinement, style consistency, professional finish', suggestedOrder: 5 },
  { key: 'humanizer', displayName: 'Humanizer', emoji: '\uD83D\uDDD2\uFE0F', description: 'Detect and remove AI-generated writing patterns for natural human voice', suggestedOrder: 6 },
  { key: 'format', displayName: 'Format Editor', emoji: '\uD83D\uDCD6', description: 'E-book structure: chapter breaks, front/back matter, heading hierarchy, metadata', suggestedOrder: 7 },
]

// ============================================================================
// AI Ops Types (Throttle, Budget, Forecast)
// ============================================================================

export interface ThrottleSnapshot {
  value: number
  band: string
  purposePolicies: Record<string, string>
  updatedAt: string
}

export interface BudgetSnapshot {
  month: string
  capUsd: number | null
  usedUsd: number
  forecastUsd: number | null
  remainingUsd: number | null
  enforcementMode: string
  alertThresholds: number[]
  firedAlerts: number[]
}

export interface CostForecast {
  month: string
  usedUsd: number
  forecastBaseline: number
  forecastExpected: number
  forecastHighVolume: number
  capUsd: number | null
  enforcementMode: string
  daysRemaining: number
}

export interface TokenUsageSummary {
  period: string
  totalCalls: number
  totalInputTokens: number
  totalOutputTokens: number
  estimatedCostUsd: number
  byProvider: Record<string, { calls: number; inputTokens: number; outputTokens: number; costUsd: number }>
  byPurpose: Record<string, { calls: number; inputTokens: number; outputTokens: number; costUsd: number }>
}

export interface AiOpsEvent {
  id: string
  type: 'throttle' | 'budget'
  created_at: string
  // Throttle event fields
  old_value?: number
  new_value?: number
  old_band?: string
  new_band?: string
  reason?: string
  actor?: string
  // Budget event fields
  month?: string
  threshold_pct?: number
  action_taken?: string
  details?: unknown
}

export interface RoutingState {
  config: {
    defaultTier: string
    purposeTiers: Record<string, string>
    preferLocal: boolean
    councilTier: string
  }
  resolvedRoutes: Record<string, Record<string, Array<{ provider: string; model: string }>>>
  timestamp: string
}

// ============================================================================
// SAFE Review Types
// ============================================================================

export interface SafeReviewFinding {
  id: string
  category: 'S' | 'A' | 'F' | 'E'
  categoryLabel: string
  severity: 'critical' | 'warning' | 'info'
  location: string
  excerpt: string
  issue: string
  suggestion: string
}

export interface SafeReviewResult {
  id: string
  title: string
  reviewedAt: string
  summary: string
  overallRisk: 'high' | 'medium' | 'low' | 'clear'
  findings: SafeReviewFinding[]
  stats: {
    total: number
    critical: number
    warning: number
    info: number
    byCategory: { S: number; A: number; F: number; E: number }
  }
  inputTokens: number
  outputTokens: number
}

export interface SafeReviewListItem {
  id: string
  title: string
  reviewedAt: string
  overallRisk: string
  total: number
  critical: number
}
